/* eslint-disable operator-linebreak */
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import oktaAuth from './configs/oktaAuth';
import { ERROR_WHITELIST } from './configs/errorWhitelist';
import store from '../redux/store';
import { isJwtExpiredError, triggerSessionExpiredLogout } from '../common/sessionLogout';

const pickErrorProperties = (error: any, whitelist: any) => {
    if (error === null || typeof error !== 'object') {
        return error;
    }

    const picked: any = {};
    for (const key in whitelist) {
        if (whitelist[key] === true && key in error) {
            picked[key] = error[key];
        } else if (typeof whitelist[key] === 'object' && key in error) {
            picked[key] = pickErrorProperties(error[key], whitelist[key]);
        }
    }
    return picked;
};

const getUserTenant = () => {
    const currentState = store.getState();
    return currentState?.auth?.user?.tenant || '';
};

const mockApiInstance: any = axios.create({
    baseURL: `${process.env.REACT_APP_MOCK_API_URL || 'http://localhost:4000'}`
});

export const uipApiInstance = axios.create({
    baseURL: process.env.REACT_APP_UIP_API_URL
});

axios.interceptors.request.use(
    async (config: any) => {
        const uuid = uuidv4();
        config.headers['X-CSRF-Token'] = uuid;

        return config;
    },
    (error: any) => Promise.reject(error)
);

uipApiInstance.interceptors.request.use(
    async (config: any) => {
        let tokens = await oktaAuth.tokenManager.getTokens();
        let accessToken = tokens?.accessToken;

        // hasExpired() is a *soft* check: it reports true during Okta's early-renew
        // window (expireEarlySeconds) while autoRenew's network call is still in flight
        // and storage still holds the old token. Renew first - tokenManager.renew()
        // dedupes with any in-flight autoRenew and returns the fresh token - and only
        // fall back to a session-expired logout if that renew genuinely fails.
        if (accessToken && oktaAuth.tokenManager.hasExpired(accessToken)) {
            try {
                await oktaAuth.tokenManager.renew('accessToken');
                tokens = await oktaAuth.tokenManager.getTokens();
                accessToken = tokens?.accessToken;
            } catch (renewError) {
                console.log(
                    '[session-auth] Request interceptor: token renew failed before request to',
                    config?.url,
                    '- cancelling and logging out',
                    renewError
                );
                triggerSessionExpiredLogout();
                return Promise.reject(new axios.Cancel('Session expired'));
            }
        }

        if (tokens) {
            config.headers.Authorization = 'Bearer ' + accessToken?.accessToken;
        }

        const uuid = uuidv4();
        config.headers['X-CSRF-Token'] = uuid;

        if (config.method && config.method.toLowerCase() === 'post' && config.data && typeof config.data === 'object') {
            const tenant = getUserTenant();
            if (tenant != '') {
                if (config.data instanceof FormData) {
                    config.data.append('from_tenant', tenant);
                    return config;
                }
                config.data = { ...config.data, from_tenant: tenant };
            }
        }

        return config;
    },
    (error: any) => Promise.reject(error)
);

uipApiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        // Ignore the proactive request cancellation we raise on an expired token.
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }
        if (isJwtExpiredError(error)) {
            // Access token rejected (e.g. tab left idle until expiry). Trigger a
            // single logout and skip the noisy error log for the burst of
            // concurrent 401s.
            console.log(
                '[session-auth] Response interceptor: JWT expired / 401 from',
                error?.config?.url,
                '\u2013 triggering logout'
            );
            triggerSessionExpiredLogout();
        } else {
            console.error('API Error:', error);
        }
        const pickedError = pickErrorProperties(error, ERROR_WHITELIST);
        return Promise.reject(pickedError);
    }
);

export default mockApiInstance;
